import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import { IconButton, Tooltip } from '@mui/material';
import React from 'react';
import { useAppDispatch } from '../../app/hooks';
import { copyCartItemToDraft } from '../../redux/features/draftCart/draftCart.thunks';
import { CartItem } from '../../utils/cart';
import Colors from '../../utils/color';

/**
 * "item", not "line". `line` is receipt vocabulary and appears nowhere else the
 * agent can see — the column header counts "items", and the controls in the same
 * row say Duplicate and Remove. Mirrored by `PROMOTE_LINE_LABEL`.
 */
export const COPY_TO_DRAFT_LABEL = 'Copy this item into My Cart';

/**
 * The anchor the control positions itself against. A module-level frozen inline
 * style rather than a `makeStyles` rule: adding a rule moves JSS's global
 * counter and renumbers class names in every snapshot that renders this
 * component, and `CartItemReview` is snapshotted. It is also applied only when
 * the control is rendered, so the flag-off tree is unchanged down to the node.
 */
export const COPY_ANCHOR_STYLE: React.CSSProperties = Object.freeze({
  position: 'relative',
});

interface CopyToDraftControlProps {
  cartItem: CartItem;
}

/**
 * HITL-1614 — take one line from the AI.
 *
 * A 20px circle centred ON the AI card's left border, so it costs the column
 * 6px of width rather than the 26px a full-height rail would. The `::before`
 * inset grows the hit target back to a comfortable 34px without growing the
 * visible circle.
 *
 * `KeyboardDoubleArrowLeftIcon`, deliberately not `ContentCopyOutlinedIcon`:
 * copy already means *Duplicate this line* in the same row, and the double
 * arrow points at the column the item is going to.
 *
 * `stopPropagation` matters here — the card's own `onClick` selects the line
 * and toggles its collapse, and pressing this must do neither.
 *
 * `sx` rather than `makeStyles` for the same counter reason as above: emotion
 * hashes on content, so a brand-new element cannot renumber an existing one.
 */
const CopyToDraftControl = ({ cartItem }: CopyToDraftControlProps) => {
  const dispatch = useAppDispatch();

  const onCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(copyCartItemToDraft(cartItem));
  };

  return (
    <Tooltip title={COPY_TO_DRAFT_LABEL} arrow disableInteractive>
      <IconButton
        aria-label={COPY_TO_DRAFT_LABEL}
        data-testid="copy-to-draft"
        onClick={onCopy}
        // The card's onClick fires on mouse events that bubble; the card also
        // reads mousedown elsewhere, so stop both.
        onMouseDown={(e) => e.stopPropagation()}
        size="small"
        sx={{
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
          width: 20,
          height: 20,
          padding: 0,
          backgroundColor: Colors.white,
          border: `1px solid ${Colors.alto}`,
          color: Colors.readBack,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: -7,
          },
          '&:hover': {
            backgroundColor: Colors.readBack,
            color: Colors.white,
            borderColor: Colors.readBack,
          },
          '& .MuiSvgIcon-root': { fontSize: '0.85rem' },
        }}
      >
        <KeyboardDoubleArrowLeftIcon />
      </IconButton>
    </Tooltip>
  );
};

export default CopyToDraftControl;
